/**
 * WebGPURenderer setup. three.js tries WebGPU first and falls back to its
 * WebGL2 backend when WebGPU is unavailable (WebGPURenderer constructor,
 * three r186). After init() we read which backend actually came up.
 */
import { WebGPURenderer } from 'three/webgpu';

export type ActiveBackend = 'webgpu' | 'webgl2';

export interface RendererHandle {
  readonly renderer: WebGPURenderer;
  readonly backend: ActiveBackend;
  readonly canvas: HTMLCanvasElement;
  setRenderScale(scale: number): void;
  /** Resize to the container. Returns the new aspect ratio. */
  resize(): number;
  dispose(): void;
}

export async function createRenderer(
  container: HTMLElement,
  renderScale: number,
  onDeviceLost: (reason: string) => void,
  forceWebGL = false,
): Promise<RendererHandle> {
  // trackTimestamp: GPU timing for the governor (core/gpuTiming.ts). three.js
  // disables it by itself when the device lacks timestamp queries.
  const renderer = new WebGPURenderer({ antialias: true, forceWebGL, trackTimestamp: true });
  await renderer.init();

  // WebGPUBackend sets isWebGPUBackend; WebGLBackend sets isWebGLBackend.
  const backendFlags = renderer.backend as { isWebGPUBackend?: boolean };
  const backend: ActiveBackend = backendFlags.isWebGPUBackend === true ? 'webgpu' : 'webgl2';

  // Both backends route loss through onDeviceLost (WebGL via webglcontextlost).
  renderer.onDeviceLost = (info) => {
    onDeviceLost(info.message);
  };

  const canvas = renderer.domElement;
  container.appendChild(canvas);
  let scale = renderScale;

  function resize(): number {
    const w = container.clientWidth || window.innerWidth;
    const h = container.clientHeight || window.innerHeight;
    renderer.setPixelRatio(window.devicePixelRatio * scale);
    renderer.setSize(w, h, true);
    return w / h;
  }
  resize();

  return {
    renderer,
    backend,
    canvas,
    setRenderScale(s: number): void {
      scale = s;
      resize();
    },
    resize,
    dispose(): void {
      try {
        // dispose() may return a promise on some backends; nothing waits on it.
        void Promise.resolve(renderer.dispose()).catch((err: unknown) => {
          console.warn('[renderer] dispose failed', err);
        });
      } catch (err) {
        console.warn('[renderer] dispose failed', err);
      }
      canvas.remove();
    },
  };
}
